import { ContentCategoryId } from '@/constants';
import { fetchAllFromCache, FetchAPIOptions } from './api';
import { fetchAllFromWordpressAndCache } from './wordpress-api';
import { CONTENT_CATEGORY_LABEL } from '@/constants/content-categoryId';

export interface Item {
  id: string;
  title: string;
  content: string;
  slug?: string;
  date: string;
  lang: string;
  order?: number;
  featuredImage?: string;
}

const GET_ITEMS_QUERY = `
query get_items($categoryId: Int!, $postCount: Int!, $after: String) {
  posts(
    first: $postCount,
    where: {
      categoryId: $categoryId,
      status: PUBLISH
    },
    after: $after
  ) {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        id
        title
        content
        date
        slug
        meta {
          date
          lang
          order
        }
        featuredImage {
          node {
            mediaItemUrl
          }
        }
      }
    }
  }
}`;

/**
 * WordPress에서 아이템을 가져와 S3에 캐시 (refresh용)
 */
const fetchItemsFromWordpress = async (
  categoryId: ContentCategoryId,
  label: string,
  options?: { prodFilter?: boolean },
) => {
  return fetchAllFromWordpressAndCache(
    {
      query: GET_ITEMS_QUERY,
      getVariables: cursor => ({
        categoryId,
        postCount: 100,
        after: cursor,
      }),
      getPageInfo: data => ({
        hasNextPage: data?.posts?.pageInfo?.hasNextPage || false,
        endCursor: data?.posts?.pageInfo?.endCursor || null,
      }),
      getItems: data => data?.posts?.edges || [],
    },
    { label, prodFilter: options?.prodFilter },
  );
};

/**
 * S3 캐시에서 아이템을 가져옴 (빌드/런타임용)
 */
const fetchItemsFromCache = async (label: string) => {
  return fetchAllFromCache(
    {
      getPageInfo: data => ({
        hasNextPage: data?.posts?.pageInfo?.hasNextPage || false,
      }),
      getItems: data => data?.posts?.edges || [],
      postCount: 100,
    },
    { label },
  );
};

export const getItems = async (
  categoryId: ContentCategoryId,
  options?: FetchAPIOptions,
): Promise<Item[]> => {
  try {
    const label = `${options?.label ? options.label + '-' : ''}fetchItems-${CONTENT_CATEGORY_LABEL[categoryId]}`;
    const refresh = options?.refresh ?? false;

    const data = refresh
      ? await fetchItemsFromWordpress(categoryId, label, {
          prodFilter: options?.prodFilter,
        })
      : await fetchItemsFromCache(label);

    return (
      data?.map((edge: any) => {
        return {
          id: edge.node.id,
          title: edge.node.title,
          content: edge.node.content,
          slug: edge.node.slug,
          date: edge.node.meta.date,
          lang: edge.node.meta.lang,
          order: edge.node.meta.order,
          featuredImage: edge.node.featuredImage?.node.mediaItemUrl,
        };
      }) || []
    );
  } catch (error) {
    return [];
  }
};
